import { useCallback } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { billingQueryKeys } from '@/features/billing/api/billing.api';
import { landingQueryKeys } from '@/features/dashboard/api/dashboard.api';
import { dashboardQueryKeys } from '@/features/dashboards/api/dashboards.api';
import { pendingSummaryQueryKeys } from '@/features/dashboards/api/pendingSummary.api';
import { dcrApproveQueryKeys } from '@/features/dcrApprove/api/dcrApprove.api';
import { feedbackQueryKeys } from '@/features/feedback/api/feedback.api';
import { feedbackTypeQueryKeys } from '@/features/feedbackTypes/api/feedbackTypes.api';
import { instituteQueryKeys } from '@/features/institutes/api/institutes.api';
import { instituteSubmissionQueryKeys } from '@/features/instituteSubmissions/api/instituteSubmissions.api';
import { instituteTypeQueryKeys } from '@/features/instituteTypes/api/instituteTypes.api';
import { leaveApplicationQueryKeys } from '@/features/leaveApplications/api/leaveApplications.api';
import { leaveTypeQueryKeys } from '@/features/leaveTypes/api/leaveTypes.api';
import { limitTypeQueryKeys } from '@/features/limitTypes/api/limitTypes.api';
import { marketPromotionQueryKeys } from '@/features/marketPromotion/api/marketPromotion.api';
import { mpP2QueryKeys } from '@/features/marketPromotion/api/marketPromotionP2.api';
import { productQueryKeys } from '@/features/product/api/product.api';
import { requisitionQueryKeys } from '@/features/productRequisition/api/productRequisition.api';
import { publishQueryKeys } from '@/features/publish/api/publish.api';
import { questionQueryKeys } from '@/features/question/api/question.api';
import { roleQueryKeys } from '@/features/roles/api/roles.api';
import { surveyQueryKeys } from '@/features/surveys/api/surveys.api';
import { teacherSubmissionQueryKeys } from '@/features/teacherSubmissions/api/teacherSubmissions.api';
import { userInstituteMappingQueryKeys } from '@/features/userInstituteMapping/api/userInstituteMapping.api';
import { userQueryKeys } from '@/features/users/api/users.api';
import { zonalStatementQueryKeys } from '@/features/zonalStatement/api/zonalStatement.api';

const INSTITUTE_DATA: readonly QueryKey[] = [
  instituteQueryKeys.all(),
  instituteSubmissionQueryKeys.all(),
  userInstituteMappingQueryKeys.all(),
  teacherSubmissionQueryKeys.all(),
  questionQueryKeys.all(),
  mpP2QueryKeys.all(),
  productQueryKeys.all(),
  requisitionQueryKeys.all(),
];

const RELATED_DATA_BY_CHANGE = {
  billingEntry: [
    billingQueryKeys.all(),
    dcrApproveQueryKeys.all(),
    pendingSummaryQueryKeys.all(),
    dashboardQueryKeys.all(),
    landingQueryKeys.all(),
    zonalStatementQueryKeys.all(),
    publishQueryKeys.all(),
  ],
  institute: INSTITUTE_DATA,
  instituteType: [instituteTypeQueryKeys.all(), requisitionQueryKeys.all(), ...INSTITUTE_DATA],
  instituteMapping: [
    userInstituteMappingQueryKeys.all(),
    questionQueryKeys.all(),
    mpP2QueryKeys.all(),
  ],
  limitType: [
    limitTypeQueryKeys.all(),
    billingQueryKeys.all(),
    marketPromotionQueryKeys.all(),
    mpP2QueryKeys.all(),
  ],
  leaveType: [leaveTypeQueryKeys.all(), leaveApplicationQueryKeys.all()],
  leaveApplication: [leaveApplicationQueryKeys.all()],
  feedbackType: [feedbackTypeQueryKeys.all(), feedbackQueryKeys.all()],
  catalogue: [productQueryKeys.all(), requisitionQueryKeys.all()],
  user: [userQueryKeys.all(), roleQueryKeys.all()],
  survey: [surveyQueryKeys.all()],
} as const satisfies Record<string, readonly QueryKey[]>;

export type RelatedDataChange = keyof typeof RELATED_DATA_BY_CHANGE;

export function useRefreshRelatedData() {
  const queryClient = useQueryClient();

  return useCallback(
    (change: RelatedDataChange) =>
      Promise.all(
        RELATED_DATA_BY_CHANGE[change].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      ),
    [queryClient],
  );
}
